import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useWarehouseContext } from '../../../../../../contexts/WarehouseContext';
import { ARRIVAL_POINT_TYPE_WHS } from '../../constants';
import {
  GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_PAGE_LIST,
  GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
  DeliveryProgressMonitoringOfSlaPageRequestType,
  DeliveryProgressMonitoringOfSlaDetailPageRequestType,
  DeliveryProgressMonitoringOfSlaPageResponseType,
  DeliveryProgressMonitoringOfSlaDetailPageResponseType,
  DeliveryProgressMonitoringOfSlaSummaryType,
  DeliveryProgressMonitoringOfSlaType,
  DeliveryProgressMonitoringOfSlaDetailType,
  GetDeliveryProgressMonitoringOfSlaPageListResponse,
  GetDeliveryProgressMonitoringOfSlaDetailListResponse
} from '../queries/getDeliveryProgressMonitoringOfSlaList';
import {
  GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_PAGE_LIST,
  GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
  GetDspDeliveryProgressMonitoringOfSlaPageListResponse,
  GetDspDeliveryProgressMonitoringOfSlaDetailListResponse,
} from '../queries/getDspDeliveryProgressMonitoringOfSlaList';

export type SlaQueryMode = 'dsp' | 'whs';

export const useDeliveryProgressMonitoringOfSlaQuery = (mode: SlaQueryMode): {
  summaryData: DeliveryProgressMonitoringOfSlaSummaryType | null;
  recordData: DeliveryProgressMonitoringOfSlaType[];
  loading: boolean;
  error: any;
  refetch: any;
  handleFilter: (newPageRequest: DeliveryProgressMonitoringOfSlaPageRequestType) => void;
  currentFilters: DeliveryProgressMonitoringOfSlaPageRequestType | null;
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
} => {
  const [pageRequest, setPageRequest] = useState<DeliveryProgressMonitoringOfSlaPageRequestType | null>(null);
  const [shouldQuery, setShouldQuery] = useState(false);

  // 获取当前仓库ID
  const { currentWarehouse } = useWarehouseContext();
  const warehouseId = currentWarehouse?.id!;

  const isDsp = mode === 'dsp';

  const { data, loading, error, refetch } = useQuery<GetDeliveryProgressMonitoringOfSlaPageListResponse | GetDspDeliveryProgressMonitoringOfSlaPageListResponse>(
    isDsp
      ? GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_PAGE_LIST
      : GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_PAGE_LIST,
    {
      variables: {
        pageRequest: pageRequest || {
          filter: {
            arrivalDateStart: new Date().toISOString().split('T')[0],
            arrivalDateEnd: new Date().toISOString().split('T')[0],
            warehouseIds: [Number(warehouseId)],
            dspIds: undefined,
            areaIds: undefined,
            cityNames: undefined,
            driverIds: undefined,
            arrivalPointType: ARRIVAL_POINT_TYPE_WHS,
            dimension: 'area',
          },
          pageRequest: {
            page: 1,
            size: 20,
            sortBy: 'id',
            sortDirection: 'DESC'
          }
        }
      },
      skip: !shouldQuery || !pageRequest, // 只有在有分页请求且应该查询时才查询
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  const slaPageList: DeliveryProgressMonitoringOfSlaPageResponseType | undefined = isDsp
    ? (data as GetDspDeliveryProgressMonitoringOfSlaPageListResponse | undefined)?.getDeliveryProgressMonitoringOfSlaPageListForDsp
    : (data as GetDeliveryProgressMonitoringOfSlaPageListResponse | undefined)?.getDeliveryProgressMonitoringOfSlaPageList;

  const handleFilter = (newPageRequest: DeliveryProgressMonitoringOfSlaPageRequestType) => {
    setPageRequest(newPageRequest);
    setShouldQuery(true);
  };

  return {
    summaryData: slaPageList?.summaryData || null,
    recordData: slaPageList?.pageResponse?.content || [],
    loading,
    error,
    refetch,
    handleFilter,
    currentFilters: pageRequest,
    pagination: slaPageList?.pageResponse ? {
      page: slaPageList.pageResponse.page,
      size: slaPageList.pageResponse.size,
      totalElements: slaPageList.pageResponse.totalElements,
      totalPages: slaPageList.pageResponse.totalPages,
      hasNext: slaPageList.pageResponse.hasNext,
      hasPrevious: slaPageList.pageResponse.hasPrevious,
    } : null,
  };
};

export const useDeliveryProgressMonitoringOfSlaDetailQuery = (mode: SlaQueryMode): {
  detailData: DeliveryProgressMonitoringOfSlaDetailType[];
  loading: boolean;
  error: any;
  refetch: any;
  handlePageRequest: (pageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType) => void;
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
} => {
  const [pageRequest, setPageRequest] = useState<DeliveryProgressMonitoringOfSlaDetailPageRequestType | null>(null);
  const [shouldQuery, setShouldQuery] = useState(false);

  // 获取当前仓库ID
  const { currentWarehouse } = useWarehouseContext();
  const warehouseId = currentWarehouse?.id!;

  const isDsp = mode === 'dsp';

  const { data, loading, error, refetch } = useQuery<GetDeliveryProgressMonitoringOfSlaDetailListResponse | GetDspDeliveryProgressMonitoringOfSlaDetailListResponse>(
    isDsp
      ? GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST
      : GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
    {
      variables: {
        pageRequest: pageRequest || {
          filter: {
            arrivalDateStart: new Date().toISOString().split('T')[0],
            arrivalDateEnd: new Date().toISOString().split('T')[0],
            warehouseIds: [Number(warehouseId)],
            dspIds: undefined,
            areaIds: undefined,
            cityNames: undefined,
            trackingNumber: undefined,
            statusField: undefined,
            arrivalPointType: ARRIVAL_POINT_TYPE_WHS,
          },
          page: 0,
          size: 10,
          sortBy: 'createdAt',
          sortDirection: 'DESC'
        }
      },
      skip: !shouldQuery || !pageRequest,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  const detailList: DeliveryProgressMonitoringOfSlaDetailPageResponseType | undefined = isDsp
    ? (data as GetDspDeliveryProgressMonitoringOfSlaDetailListResponse | undefined)?.getDeliveryProgressMonitoringOfSlaDetailListForDsp
    : (data as GetDeliveryProgressMonitoringOfSlaDetailListResponse | undefined)?.getDeliveryProgressMonitoringOfSlaDetailList;

  const handlePageRequest = (newPageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType) => {
    setPageRequest(newPageRequest);
    setShouldQuery(true);
  };

  return {
    detailData: detailList?.content || [],
    loading,
    error,
    refetch,
    handlePageRequest,
    pagination: detailList ? {
      page: detailList.page,
      size: detailList.size,
      totalElements: detailList.totalElements,
      totalPages: detailList.totalPages,
      hasNext: detailList.hasNext,
      hasPrevious: detailList.hasPrevious,
    } : null,
  };
};
